import React from 'react';
import PropTypes from 'prop-types';
import { NumberFormatter } from '@mantine/core';
import { ChevronDown, ChevronUp } from 'tabler-icons-react';
import AppFlexbox from '../../common/AppFlexbox';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';

const calculateTaxInfo = (taxItems = []) => {
  const taxMap = new Map();
  const taxRates = [];
  let taxAmount = 0;

  for (const { tax } of taxItems) {
    let rateSum = 0;
    let amountSum = 0;

    for (const t of tax) {
      rateSum += t.taxRate ?? 0;
      amountSum += t.taxAmount;

      const key = `${t.name}_${t.taxRate}`;

      const existing = taxMap.get(key);
      if (existing) {
        existing.taxAmount += t.taxAmount;
      }
      else {
        taxMap.set(key, { ...t });
      }
    }

    if (rateSum > 0) {
      taxRates.push(rateSum);
    }

    taxAmount += amountSum;
  }

  return {
    taxAmount,
    taxRates,
    taxInfo: Array.from(taxMap.values())
  };
};

const OrderProductsTaxInfo = ({ taxItems, justifyContent, disableDetails }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const totalTaxInfo = calculateTaxInfo(taxItems);

  const minTaxRate =
    totalTaxInfo.taxRates.length > 0 ? Math.min(...totalTaxInfo.taxRates) : 0;
  const maxTaxRate =
    totalTaxInfo.taxRates.length > 0 ? Math.max(...totalTaxInfo.taxRates) : 0;

  return (
    <AppStack
      onClick={() => {
        if (!disableDetails) {
          setIsExpanded(!isExpanded);
        }
      }}
      style={{
        cursor: disableDetails ? 'default' : 'pointer',
        gap: 0,
        justifyContent: justifyContent || 'flex-end'
      }}
    >
      <AppFlexbox
        style={{
          gap: 5,
          justifyContent: justifyContent || 'flex-end'
        }}
      >
        <AppText style={{ fontSize: 14, color: '#666', textWrap: 'nowrap' }}>
          <NumberFormatter
            decimalScale={2}
            displayType="text"
            fixedDecimalScale
            prefix="+$"
            thousandSeparator
            value={totalTaxInfo.taxAmount / 100}
          />{' '}
          @{' '}
          <>
            <NumberFormatter
              decimalScale={2}
              displayType="text"
              suffix="%"
              thousandSeparator
              value={minTaxRate}
            />
            {minTaxRate !== maxTaxRate && (
              <>
                {' - '}
                <NumberFormatter
                  decimalScale={2}
                  displayType="text"
                  suffix="%"
                  thousandSeparator
                  value={maxTaxRate}
                />
              </>
            )}
          </>
        </AppText>
        {!disableDetails &&
          (isExpanded ? (
            <ChevronUp color="#666" size={16} style={{ marginTop: 5 }} />
          ) : (
            <ChevronDown color="#666" size={16} style={{ marginTop: 5 }} />
          ))}
      </AppFlexbox>

      {isExpanded && (
        <AppStack style={{ gap: 0 }}>
          {totalTaxInfo?.taxInfo.map((t) => (
            <AppFlexbox
              key={t.name}
              style={{
                gap: 5,
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#666'
              }}
            >
              <AppText style={{ fontSize: 14 }}>
                {t.name} (
                {t.taxRate === null ? (
                  <>
                    <NumberFormatter
                      decimalScale={2}
                      displayType="text"
                      suffix="%"
                      thousandSeparator
                      value={minTaxRate}
                    />
                    {minTaxRate !== maxTaxRate && (
                      <>
                        {' - '}
                        <NumberFormatter
                          decimalScale={2}
                          displayType="text"
                          suffix="%"
                          thousandSeparator
                          value={maxTaxRate}
                        />
                      </>
                    )}
                  </>
                ) : (
                  <NumberFormatter
                    decimalScale={2}
                    displayType="text"
                    suffix="%"
                    thousandSeparator
                    value={(t.taxRate / 100) * 100}
                  />
                )}
                )
              </AppText>
              <AppFlexbox>
                <AppText style={{ fontSize: 14, textWrap: 'nowrap', flex: 1 }}>
                  <NumberFormatter
                    decimalScale={2}
                    displayType="text"
                    fixedDecimalScale
                    prefix="$"
                    thousandSeparator
                    value={t.taxAmount / 100}
                  />
                </AppText>
              </AppFlexbox>
            </AppFlexbox>
          ))}
        </AppStack>
      )}
    </AppStack>
  );
};

OrderProductsTaxInfo.propTypes = {
  disableDetails: PropTypes.bool,
  justifyContent: PropTypes.string,
  taxItems: PropTypes.array
};

export default OrderProductsTaxInfo;
